'use client'

import { useCallback, useContext, useMemo, useState } from 'react'
import clsx from 'clsx'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { LoginModal } from 'modules/auth'
import { useCustomerData } from 'modules/customer'
import { SeoListName } from 'shared/analytics'
import { IS_TMS_DELIVERY } from 'shared/config'
import { Collapse } from 'shared/ui/Collapse'
import type { CollapseProps } from 'shared/ui/Collapse/Collapse'
import OfflineNotificationLayer from 'shared/ui/Offline'
import {
  OfflineContext,
  OfflineContextType
} from 'shared/ui/Offline/OfflineContext'
import { getPathFromDestination, PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import {
  addShippingInfo,
  beginCheckout,
  viewCartList
} from '../../../analytics'
import { CartPorts } from '../../../ports'
import useCartStore, { useDisplayCart } from '../../../store/cartStore'
import { PackingOption } from '../../../types'
import CartActiveBenefitCodes from '../../appliedBenefitCodes/components/CartActiveBenefitCodes'
import CartBenefitCodesForm from '../../appliedBenefitCodes/components/CartBenefitCodesForm'
import useDiscountCodes from '../../appliedBenefitCodes/hooks/useDiscountCodes'
import useCartValidation from '../hooks/useCartValidation'
import { IS_PACKING_COST_ENABLED } from '../utils'

import CartPackingCost from './CartPackingCost'
import CartSummaryButton from './CartSummaryButton'
import CartSummaryDiscountSection from './CartSummaryDiscountSection'
import CartSummaryPriceItem from './CartSummaryPriceItem'
import MinValueInfoBox from './MinValueInfoBox'

import styles from './CartPricingSummary.module.scss'

type CartPagePricingTypes = {
  destination: string
  isCartDrawer?: boolean
  isExpanded?: boolean
  isMobile?: boolean
  handleModalClose?: () => void
  productsQuantityExceeded?: boolean
  ports: CartPorts
}

const CartPricingSummary = ({
  destination,
  isExpanded,
  isCartDrawer = false,
  isMobile = false,
  handleModalClose,
  ports
}: CartPagePricingTypes) => {
  const {
    productsNotAvailableInStock,
    productsQuantityExceeded,
    setPackingOption,
    isLoading,
    productsNotAvailableForDeliveryMethod
  } = useCartStore()
  const cart = useDisplayCart()
  const session = useSession()
  const [loginModalOpen, setLoginModalOpen] = useState(false)

  const { isOnline } = useContext(OfflineContext) as OfflineContextType
  const { user, token } = useCustomerData()
  const router = useRouter()
  const pathname = usePathname()

  const rebateCodes = useDiscountCodes(cart)

  const cartState = useMemo(() => {
    const hasPromoCode = cart?.discountCodesTotalValue?.value !== 0
    const promoCodesDiscount = hasPromoCode
      ? `-${cart?.discountCodesTotalValue?.formatted ?? ''}`
      : ''
    const hasSelectedSlot = IS_TMS_DELIVERY
      ? isNotNilOrEmpty(cart?.tmsReservationSlotData)
      : isNotNilOrEmpty(cart?.deliverySlotId)
    const missingValueForFreeDelivery =
      cart?.missingValueForFreeDelivery?.value || null
    const minValueForFreeDelivery =
      cart?.deliverySlot?.deliveryMethod?.minOrderValueForFreeDelivery?.value ||
      null
    const minOrderValue = cart?.minOrderValue?.value || null
    const minOrderValueMissing = cart?.minOrderValueMissing?.value || null
    const deliveryCost = hasSelectedSlot
      ? cart?.deliveryCost?.formatted || '0,00 zł'
      : 'Wybierz termin'
    const savings = cart?.savingsTotal?.formatted || null
    const itemsPrice = cart?.itemsValue?.gross?.formatted || '0,00 zł'
    const totalPrice = cart?.paymentValue?.formatted || '0,00 zł'
    const products = cart?.items || []
    const validItemsForDiscountCode =
      cart?.areDiscountCodesEligibleToUse || false

    return {
      hasPromoCode,
      promoCodesDiscount,
      hasSelectedSlot,
      missingValueForFreeDelivery,
      minValueForFreeDelivery,
      minOrderValue,
      minOrderValueMissing,
      deliveryCost,
      savings,
      itemsPrice,
      totalPrice,
      products,
      validItemsForDiscountCode
    }
  }, [cart])

  const pageType = useMemo(() => {
    switch (pathname) {
      case PATHS.delivery:
        return 'delivery'
      case PATHS.payment:
        return 'payment'
      case PATHS.cart:
      default:
        return 'cart'
    }
  }, [pathname])

  const disableConditionErrors = useCartValidation({
    cart,
    cartState,
    pageType,
    productsNotAvailableInStock,
    productsQuantityExceeded,
    productsNotAvailableForDeliveryMethod
  })

  const isPaymentPage = pageType === 'payment'
  const isCartEditMode = cart?.editMode ?? false

  const handleOpenLoginModal = useCallback(() => setLoginModalOpen(true), [])
  const handleCloseLoginModal = useCallback(() => setLoginModalOpen(false), [])

  const redirectToCart = useCallback(async () => {
    if (pathname === PATHS.cart && handleModalClose) {
      handleModalClose()
    } else {
      router.push(PATHS.cart)
    }
  }, [pathname, router, handleModalClose])

  const handleBeginCheckoutAnaliticEvent = useCallback(() => {
    const cartItems = cart?.items || []

    if (!isNotNilOrEmpty(cartItems) || !user) return

    if (isNotNilOrEmpty(cartItems)) {
      viewCartList({
        user,
        products: cartItems,
        listName: SeoListName.CART_ORDER_PRODUCTS
      })
      beginCheckout({
        user,
        products: cartItems,
        cart
      })
    }
  }, [cart, user])

  const moveTo = useCallback(async () => {
    if (destination === 'payment') {
      addShippingInfo({
        cart,
        user
      })
    }

    if (destination === 'delivery') {
      handleBeginCheckoutAnaliticEvent()
    }

    void router.push(getPathFromDestination(destination))
  }, [destination, user, cart, router, handleBeginCheckoutAnaliticEvent])

  const needsAuth = pathname === PATHS.cart && !token

  const handlePackingOptionChange = useCallback(
    (option: PackingOption) => {
      void setPackingOption(option, session)
    },
    [setPackingOption, session]
  )

  return (
    <>
      <div className={styles.wrapper} id='cart-summary'>
        <div className={styles.tabletContent}>
          <div
            className={styles.sectionTitle}
            {...addTestId('cart-summary-title')}
          >
            Podsumowanie
          </div>
        </div>

        {rebateCodes.activeCodes.length > 0 && (
          <CartActiveBenefitCodes
            activeCodes={rebateCodes.activeCodes}
            handleRemoveCode={rebateCodes.handleRemoveCode}
          />
        )}
        <StyledCollapse
          open={!isMobile || !!isExpanded}
          transitionDuration={isMobile ? 300 : 0}
        >
          <>
            <div className={styles.pricingDetails}>
              {IS_PACKING_COST_ENABLED && (
                <CartPackingCost
                  selectedPackingOption={cart?.package || null}
                  onChange={handlePackingOptionChange}
                  cartId={cart?.id || ''}
                  token={token}
                />
              )}

              <CartSummaryPriceItem
                tag='cart-summary-price-items'
                label='Wartość produktów'
                amount={cartState.itemsPrice}
              />

              {cartState.promoCodesDiscount && (
                <CartSummaryPriceItem
                  tag='cart-summary-rebate-codes'
                  label='Kody rabatowe'
                  amount={cartState.promoCodesDiscount}
                />
              )}
              <CartSummaryPriceItem
                tag='cart-summary-delivery-cost'
                label='Koszt dostawy'
                amount={cartState.deliveryCost}
              />
            </div>

            {cartState.savings && (
              <CartSummaryDiscountSection
                tag='cart-summary-discount-section'
                savings={cartState.savings}
              />
            )}
            <CartSummaryPriceItem
              tag='cart-summary-total-price'
              label='Do zapłaty'
              type='total'
              amount={cartState.totalPrice}
            />
            {isNotNilOrEmpty(cartState.minValueForFreeDelivery) &&
              isNotNilOrEmpty(cartState.missingValueForFreeDelivery) && (
                <div className={styles.row} aria-live='polite'>
                  <MinValueInfoBox
                    valueMissing={cartState.missingValueForFreeDelivery}
                    type='delivery'
                  />
                </div>
              )}
            {isNotNilOrEmpty(cartState.minOrderValue) &&
              isNotNilOrEmpty(cartState.minOrderValueMissing) && (
                <div className={styles.row} aria-live='polite'>
                  <MinValueInfoBox
                    valueMissing={cartState.minOrderValueMissing}
                    type='orderMissing'
                  />
                </div>
              )}
          </>
        </StyledCollapse>

        <div className={styles.buttonsWrapper}>
          <CartBenefitCodesForm
            rebateCodes={rebateCodes}
            cart={cart}
            isLoading={isLoading}
          />

          {isPaymentPage && isCartEditMode && (
            <ports.EditSaleOrderNotice pageType={pageType} variant='buttons' />
          )}
          <CartSummaryButton
            pageType={pageType}
            isPaymentPage={isPaymentPage}
            disableConditionErrors={disableConditionErrors}
            isCartDrawer={isCartDrawer}
            isMobile={isMobile}
            redirect={redirectToCart}
            authorize={needsAuth ? handleOpenLoginModal : undefined}
            navigate={moveTo}
            isLoading={isLoading}
            paymentValue={cartState.totalPrice}
            ports={ports}
          />
        </div>
        {!isOnline && <OfflineNotificationLayer />}
      </div>
      <LoginModal open={loginModalOpen} onClose={handleCloseLoginModal} />
    </>
  )
}

export default CartPricingSummary

const StyledCollapse = ({ className, ...props }: CollapseProps) => (
  <Collapse className={clsx(styles.collapse, className)} {...props} />
)
